import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@my-saas/ui";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import {
  useLocationGroupMutations,
  useLocationGroups,
  useOrefCities,
} from "@/client/services/tenant-config";

/**
 * Location groups editor.
 * - List groups, create/rename/delete.
 * - Per group, edit the city list via a searchable picker + a chip removal UX.
 *
 * Saving cities triggers a settings reload event so the in-memory tenant
 * runtime recomputes its location-labels set immediately.
 */
export const LocationGroupsPage = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: groups, isPending } = useLocationGroups(tenantSlug);
  const { create, update, remove, setCities } = useLocationGroupMutations(tenantSlug);
  const [newName, setNewName] = useState("");
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const { data: cities } = useOrefCities(tenantSlug, search.length >= 2 ? search : undefined);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await create.mutateAsync({ name: newName.trim() });
    setNewName("");
    toast({ title: "Location group created" });
  };

  const handleAddCity = async (groupId: string, current: string[], cityValue: string) => {
    if (current.includes(cityValue)) return;
    await setCities.mutateAsync({ id: groupId, cityValues: [...current, cityValue] });
  };

  const handleRemoveCity = async (groupId: string, current: string[], cityValue: string) => {
    await setCities.mutateAsync({
      id: groupId,
      cityValues: current.filter((v) => v !== cityValue),
    });
  };

  return (
    <>
      <Helmet>
        <title>Location groups · {tenantSlug}</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Location groups</h1>
          <p className="text-sm text-muted-foreground">
            Groups of OREF cities. Used by card mappings to scope which alerts
            trigger sends. An "active" group's cities contribute to the
            tenant's default subscription set.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create group</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void handleCreate();
              }}
            >
              <Input
                placeholder="e.g. North"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button type="submit" disabled={!newName.trim() || create.isPending}>
                <PlusIcon className="mr-2 size-4" /> Create
              </Button>
            </form>
          </CardContent>
        </Card>

        {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}

        {groups?.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">No groups yet</CardTitle>
              <CardDescription>
                Create one above. Without a group, this tenant won't subscribe
                to any OREF alerts.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {groups?.map((g) => {
          const cityValues = g.cities.map((c) => c.cityValue);
          const isOpen = openGroupId === g.id;
          return (
            <Card key={g.id}>
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    {g.name}
                    <Badge variant={g.status === "active" ? "primary" : "secondary"}>
                      {g.status}
                    </Badge>
                    <Badge variant="secondary">{cityValues.length} cities</Badge>
                  </CardTitle>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      update.mutate({
                        id: g.id,
                        status: g.status === "active" ? "paused" : "active",
                      })
                    }
                  >
                    {g.status === "active" ? "Pause" : "Activate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setOpenGroupId(isOpen ? null : g.id)}>
                    {isOpen ? "Close" : "Edit cities"}
                  </Button>
                  <Button
                    size="sm"
                    variant="error"
                    onClick={() => {
                      if (confirm(`Delete group "${g.name}"?`)) remove.mutate(g.id);
                    }}
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1" dir="rtl">
                  {g.cities.map((c) => (
                    <Badge
                      key={c.cityValue}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => handleRemoveCity(g.id, cityValues, c.cityValue)}
                      title="Click to remove"
                    >
                      {c.city.label} ×
                    </Badge>
                  ))}
                  {g.cities.length === 0 && (
                    <span className="text-xs text-muted-foreground">No cities — group inactive</span>
                  )}
                </div>

                {isOpen && (
                  <div className="mt-4 space-y-2 border-t pt-4">
                    <Label htmlFor={`search-${g.id}`}>Add cities</Label>
                    <Input
                      id={`search-${g.id}`}
                      placeholder="Search OREF cities…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="max-h-64 overflow-y-auto rounded border bg-muted/30">
                      {(cities ?? [])
                        .filter((c) => !cityValues.includes(c.value))
                        .slice(0, 200)
                        .map((c) => (
                          <button
                            key={c.value}
                            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-muted"
                            onClick={() => handleAddCity(g.id, cityValues, c.value)}
                          >
                            <span dir="rtl">{c.label}</span>
                            <span className="text-xs text-muted-foreground">{c.value}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
};
